/**
 * settingsApi.js
 *
 * API service for the (read-only) admin settings inspection endpoint.
 *
 * Settings are env-only: values are configured via environment variables and
 * applied on deploy (container recreate). There are no write calls here — the
 * kill switch, the sole live runtime control, has its own service
 * (killSwitchApi.js).
 *
 * Every function logs the outgoing request and response status to the browser
 * console. Errors are caught, logged with detail, and rethrown with a
 * consistent { message, detail } structure so callers can display meaningful
 * page-level error messages without inspecting raw HTTP responses.
 */

import apiClient from './apiClient'

const BASE = '/admin/settings';

/**
 * Normalise an axios error into a consistent shape.
 * @param {string} context - Human-readable description of the operation.
 * @param {Error} axiosError - The error thrown by apiClient.
 * @returns {Error}
 */
function buildError(context, axiosError) {
  const response = axiosError.response ?? null;
  const status = response ? response.status : 'network-error';
  const body = response ? response.data : axiosError;
  const detail =
    (body && body.detail) ||
    (body && body.message) ||
    (body instanceof Error ? body.message : axiosError.message) ||
    'Unknown error';
  const message = `[settingsApi] ${context} failed (HTTP ${status}): ${detail}`;
  console.error(message, { status, body });
  const err = new Error(message);
  err.detail = detail;
  err.status = status;
  return err;
}

/**
 * Fetch all managed settings with their effective values and sources.
 *
 * @returns {Promise<Array>} Array of setting objects:
 *   { key, value, source: 'env'|'default', type, default, description, category }
 */
export async function fetchAllSettings() {
  console.log(`[settingsApi] GET ${BASE}/`);
  try {
    const response = await apiClient.get(`${BASE}/`);
    console.log(`[settingsApi] GET ${BASE}/ → HTTP ${response.status}`);
    return response.data.settings;
  } catch (err) {
    const status = err.response?.status ?? 'network-error';
    console.log(`[settingsApi] GET ${BASE}/ → HTTP ${status}`);
    throw buildError('fetchAllSettings', err);
  }
}
